import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import axios from 'portal/utils/axios-msal'
import { saveAs } from 'file-saver'

import { colours, sizes } from '@qflow/theme'
import { ColourButton, Text, Spacer, icons } from 'portal/lib/primitives'
import { useModuleIds } from 'portal/lib/hooks'
import { CircularProgress } from '@mui/material'
import styled from 'styled-components'
import { bugsnag } from 'portal/utils/bugsnag'

import { useMixpanelEvents } from './mixpanel'

export function ReportEventCell({ item }) {
  const [downloading, setDownloading] = useState(false)

  const { trackEventClick } = useMixpanelEvents()
  const trackClick = useCallback(() => {
    trackEventClick(item.sourceId, 'Report')
  }, [item, trackEventClick])

  const { accountId, projectId } = useModuleIds()
  const onClick = useCallback(() => {
    setDownloading(true)
    trackClick()
    async function download() {
      const url =
        '/accounts/' +
        accountId +
        '/projects/' +
        projectId +
        '/reports/' +
        item.sourceId
      try {
        const response = await axios.get(url, {
          validateStatus: status => status === 200
        })

        if (response.data.readUri) {
          const fileName =
            typeof item.description === 'string' &&
            (item.description.endsWith('.csv') ||
              item.description.endsWith('.xls') ||
              item.description.endsWith('.xlsx'))
              ? item.description
              : 'report.csv'
          saveAs(response.data.readUri, fileName)
        }
      } catch (error) {
        const errorString =
          error && error.response && error.response.data
            ? JSON.stringify(error.response.data, null, 2)
            : JSON.stringify(error)

        bugsnag?.notify(new Error(errorString), event => {
          event.addMetadata('url', url)
        })
        console.error(errorString)
      } finally {
        setDownloading(false)
      }
    }

    download()
  }, [accountId, item.description, item.sourceId, projectId, trackClick])

  function isSuccess(status) {
    return status === 'Completed'
  }

  const StateIcons = {
    Pending: <icons.StatusPending />,
    Processing: (
      <div
        style={{
          color: colours.YELLOW,
          display: 'inline-block',
          paddingRight: '0.375rem'
        }}
      >
        <CircularProgress size={sizes.MEDIUM} thickness={5} color={'inherit'} />
      </div>
    ),
    Error: <icons.StatusFailed />,
    Failed: <icons.StatusFailed />
  }

  return (
    <>
      {isSuccess(item.status) && (
        <ColourButton.Trigger
          style={{ margin: '0', display: 'inline-flex' }}
          onClick={onClick}
          disabled={downloading}
        >
          {downloading ? (
            <CircularProgress
              size={sizes.LARGER}
              thickness={5}
              color={'inherit'}
            />
          ) : (
            <icons.Download />
          )}

          <Spacer.Fine />

          <StatusText dim={['Pending', 'Processing'].includes(item.status)}>
            {item.status === 'Completed' ? 'Download' : item.status}
          </StatusText>
        </ColourButton.Trigger>
      )}

      {!isSuccess(item.status) && !!StateIcons[item.status] && (
        <>
          {StateIcons[item.status]}
          <StatusTextOnly dim={['Pending', 'Processing'].includes(item.status)}>
            {item.status}
          </StatusTextOnly>
        </>
      )}
    </>
  )
}

ReportEventCell.propTypes = {
  item: PropTypes.object.isRequired
}

const StatusText = styled(Text)`
  color: inherit;
  min-width: 4rem;
  display: inline-block;
`
const StatusTextOnly = styled(Text)`
  color: inherit;
  display: inline-block;
  padding: 0 0.5rem 0 0.125rem;
`
